import React, { useEffect, useState, useRef } from "react";
import {
  Typography,
  Card,
  CardBody,
  Button,
} from "@material-tailwind/react";
import {
  CalendarIcon,
  TruckIcon,
  BanknotesIcon,
  ExclamationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import api from "@/services/api";
import { useDispatch, useSelector } from "react-redux";
import { getDeliveries, getEarningStatement, getRejectedDeliveryAssignments } from "@/store/slices/newDeliverySlice";
import moment from "moment";
import { Link, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import InfiniteScroll from "react-infinite-scroll-component";
import ShowLoader from "@/components/Loader";
import RejectedProofModal from "./rejected-proof-modal";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";

function extractOrders(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.orders)) return payload.orders;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
}

function extractTotalPages(payload) {
  if (!payload || typeof payload !== "object") return 1;
  const tp =
    payload.total_pages ??
    payload.last_page ??
    payload.meta?.last_page ??
    payload.pagination?.last_page;
  const n = Number(tp);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

export function DeliveryHomePage() {
  const { t } = useTranslation("delivery");
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [deliveryList, setDeliveryList] = useState([]);
  const [fetching, setFetching] = useState(false);
  // const activeTab = searchParams.get("tab") === "completed" ? "completed" : "assigned";

  const tabParam = searchParams.get("tab");

  const activeTab =
    tabParam === "completed"
      ? "completed"
      : tabParam === "in_progress"
      ? "in_progress"
      : "assigned";

  const [dateRange, setDateRange] = useState({
    start_date: null,
    end_date: null,
  });

  const { deliveries, list_loading, rejected_assignments, rejected_loading, } = useSelector((state) => state.delivery);
  const [openRejectedModal, setOpenRejectedModal] = useState(false);
  const requestIdRef = useRef(0);

  const { user } = useSelector((state) => state.user);

  const { new_order_details, loading } = useSelector(
    (state) => state.delivery
  );

  useEffect(() => {
    dispatch(getRejectedDeliveryAssignments());
  }, [dispatch]);

  const handleOpenRejectedModal = async () => {
    await dispatch(getRejectedDeliveryAssignments());
    setOpenRejectedModal(true);
  };

  const assignedCount = deliveries?.assigned_deliveries ?? 0;
  const newCount = deliveries?.new_deliveries ?? 0;
  const amountEarned = deliveries?.amount_earned ?? 0;

  // const setActiveTab = (tab) => {
  //   setSearchParams(
  //     (prev) => {
  //       const next = new URLSearchParams(prev);
  //       if (tab === "completed") {
  //         next.set("tab", "completed");
  //       } else {
  //         next.delete("tab");
  //       }
  //       return next;
  //     },
  //     { replace: true }
  //   );
  // };

  const setActiveTab = (tab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        if (tab === "assigned") {
          next.delete("tab");
        } else {
          next.set("tab", tab);
        }

        return next;
      },
      { replace: true }
    );
  };

  const handleClearDate = (e) => {
    e.stopPropagation(); // Prevent the DatePicker from opening when clicking clear
    setDateRange({
      start_date: null,
      end_date: null,
    });
  };

  useEffect(() => {
    const id = ++requestIdRef.current;
    setPage(1);
    setHasMore(true);
    setDeliveryList([]);

    const run = async () => {
      setFetching(true);
      try {
        const action = await dispatch(
          getDeliveries({
            type: activeTab,
            start_date: dateRange.start_date,
            end_date: dateRange.end_date,
            page: 1,
          })
        );
        if (id !== requestIdRef.current) return;

        if (getDeliveries.fulfilled.match(action)) {
          const payload = action.payload;
          setDeliveryList(extractOrders(payload));
          setHasMore(1 < extractTotalPages(payload));
        } else {
          setDeliveryList([]);
          setHasMore(false);
        }
      } finally {
        if (id === requestIdRef.current) setFetching(false);
      }
    };

    run();
  }, [dispatch, activeTab, dateRange.start_date, dateRange.end_date]);

  const loadMore = async () => {
    if (fetching || !hasMore) return;
    const nextPage = page + 1;
    setFetching(true);
    try {
      const action = await dispatch(
        getDeliveries({
          type: activeTab,
          start_date: dateRange.start_date,
          end_date: dateRange.end_date,
          page: nextPage,
        })
      );

      if (getDeliveries.fulfilled.match(action)) {
        const payload = action.payload;
        const chunk = extractOrders(payload);
        const totalPages = extractTotalPages(payload);
        setDeliveryList((prev) => [...prev, ...chunk]);
        setPage(nextPage);
        setHasMore(nextPage < totalPages);
      } else {
        setHasMore(false);
      }
    } finally {
      setFetching(false);
    }
  };

  const initialLoading = list_loading && deliveryList.length === 0;

  const priorityColorMap = {
    high: "bg-red-500",
    medium: "bg-yellow-500",
    low: "bg-green-500",
  };

  const handleDownloadEarnings = async () => {
    try {
      const response = await api.get("delivery-partner/get-earning-statement");
      
      console.log("Full Response Data:", response.data);

      const pdfUrl = response.data?.data?.file_url || response.data?.file_url;

      if (pdfUrl) {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("API response did not contain a file URL", response.data);
      }
    } catch (error) {
      console.error("Failed to download earning statement", error);
    }
  };

  const formatPaymentStatus = (status) => {
    if (!status) return "";
    return status
      .replace(/_/g, " ")          
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="w-full max-w-7xl mx-auto py-6 sm:py-10 px-4 sm:px-6">
      {initialLoading && <ShowLoader />}

      <div className="mb-6 overflow-x-auto">
        <DatePicker
          selectsRange
          startDate={
            dateRange.start_date ? new Date(dateRange.start_date) : null
          }
          endDate={dateRange.end_date ? new Date(dateRange.end_date) : null}
          onChange={(dates) => {
            const [start, end] = dates;
            setDateRange({
              start_date: start ? moment(start).format("YYYY-MM-DD") : null,
              end_date: end ? moment(end).format("YYYY-MM-DD") : null,
            });
          }}
          customInput={
            <button
              type="button"
              className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border border-gray-200 shadow-sm text-sm font-medium whitespace-nowrap w-full sm:w-auto justify-center sm:justify-start"
            >
              <CalendarIcon className="h-5 w-5 text-gray-800 shrink-0" />
              {dateRange.start_date && dateRange.end_date
                ? `${moment(dateRange.start_date).format("DD MMM")} - ${moment(
                    dateRange.end_date
                  ).format("DD MMM YYYY")}`
                : t("select_date")}
                {/* Clear Filter Button */}
                {dateRange.start_date && (
                  <div
                    onClick={handleClearDate}
                    className="ml-2 p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4 text-red-500" />
                  </div>
                )}
            </button>
          }
        />
      </div>

     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
        {/* Card 1: New Deliveries */}
        <div
          className="relative overflow-hidden rounded-xl py-3 px-5 sm:py-4 sm:px-6 flex items-center gap-4 shadow-sm"
          style={{
            background:
              "linear-gradient(90deg, rgba(253, 243, 221, 1) 0%, rgba(244, 177, 31, 0.4) 100%)",
          }}
        >
          <div className="p-2 rounded-full shrink-0 flex items-center justify-center">
            <img
              src="/img/icons/delivery.png"
              alt="Delivery Icon"
              className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="text-xl sm:text-2xl font-bold text-gray-900">
              {newCount}
            </span>
            <span className="text-gray-800 font-medium text-xs sm:text-sm">
              {t("new_deliveries")}
            </span>
          </div>
          {newCount > 0 && (
            <div className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-[#FA7921]" />
          )}
        </div>

        {/* Card 2: Assigned Deliveries */}
        <div
        className="relative overflow-hidden rounded-xl py-3 px-5 sm:py-4 sm:px-6 flex items-center gap-4 shadow-sm border border-[#065C36]/10 bg-no-repeat bg-[position:70%_0%]"
style={{
  background: `
    linear-gradient(90deg, rgba(6, 92, 54, 0.05) 0%, rgba(6, 92, 54, 0.2) 100%),
    url('/img/icons/van.png')
  `,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "80% 0%",
}}
        >
          <div className="p-2.5 rounded-full bg-[#065C36]/10 text-[#065C36] shrink-0">
            <TruckIcon className="h-6 w-6 sm:h-8 sm:w-8" />
          </div>
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="text-xl sm:text-2xl font-bold text-gray-900">
              {assignedCount}
            </span>
            <span className="text-gray-800 font-medium text-xs sm:text-sm">
              {t("assigned_deliveries")}
            </span>
          </div>
        </div>

        {/* Card 3: Amount Earned */}
        <div
          className="rounded-xl py-3 px-5 sm:py-4 sm:px-6 flex items-center shadow-lg text-white sm:col-span-2 lg:col-span-1 bg-[url('/img/icons/earn.png')] bg-no-repeat bg-[position:80%_0%]"
          style={{ backgroundColor: "rgba(6, 92, 54, 1)" }}
        >
          <div className="flex items-center gap-4 w-full">
            <div className="p-2 rounded-lg shrink-0">
              <img
                src="/img/icons/earned.png"
                alt="Earnings Icon"
                className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <span className="text-xl sm:text-2xl font-bold whitespace-nowrap">
                 {new_order_details?.rfq_currency_code ?? ''}  {Number(amountEarned || 0).toLocaleString()}
              </span>
              <span className="text-[10px] sm:text-sm font-medium opacity-90 whitespace-nowrap tracking-wide">
                {t("amount_earned")}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8">

        {/* Tabs */}
        <div className="flex justify-center sm:justify-start">
          <div className="inline-flex p-1 gap-1.5 rounded-lg bg-[#F5F5F5] border border-gray-100 shadow-inner">

            <Button
              size="sm"
              onClick={() => setActiveTab("assigned")}
              className={`rounded-md capitalize shadow-none font-semibold px-8 py-2.5 text-sm transition-colors duration-200 ${
                activeTab === "assigned"
                  ? "bg-[#F4B11F] text-white"
                  : "bg-transparent text-[#616161] hover:bg-white/50"
              }`}
            >
              {t("assigned")}
            </Button>

            <Button
              size="sm"
              onClick={() => setActiveTab("in_progress")}
              className={`rounded-md capitalize shadow-none font-semibold px-8 py-2.5 text-sm transition-colors duration-200 ${
                activeTab === "in_progress"
                  ? "bg-[#F4B11F] text-white"
                  : "bg-transparent text-[#616161] hover:bg-white/50"
              }`}
            >
              {t("in_progress")}
            </Button>

            <Button
              size="sm"
              onClick={() => setActiveTab("completed")}
              className={`rounded-md capitalize shadow-none font-semibold px-8 py-2.5 text-sm transition-colors duration-200 ${
                activeTab === "completed"
                  ? "bg-[#F4B11F] text-white"
                  : "bg-transparent text-[#616161] hover:bg-white/50"
              }`}
            >
              {t("completed")}
            </Button>
          </div>
        </div>

        {/* Right side button */}
        <div className="flex justify-center sm:justify-end">
          <Button
            size="sm"
            onClick={handleOpenRejectedModal}
            className="flex items-center gap-2 rounded-lg bg-[#F4B11F] text-white hover:bg-[#E0A216] shadow-sm px-4 py-2"
          >
            <ExclamationTriangleIcon className="h-4 w-4" />

            <span className="font-semibold">{t("rejected_proofs")}</span>

            {(rejected_assignments?.length || 0) > 0 && (
              <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                {rejected_assignments.length}
              </span>
            )}
          </Button>
        </div>
      </div>

      {activeTab === "completed" && (
        <div className="mb-6">
          <button
            onClick={handleDownloadEarnings}
            className="flex items-center gap-2 px-5 py-3 border border-[#F4B11F] rounded-lg text-[#F4B11F] bg-white hover:bg-[#F4B11F1A] transition-colors font-medium text-l"
          >
            <img 
              src="/img/icons/statement.png" 
              alt="Statement Icon" 
              className="h-6 w-auto object-contain shrink-0" 
            />
            {t("earnings_statement")}
          </button>
        </div>
      )}

      <div
        id="scrollableDiv"
        className="min-h-[200px] max-h-[min(70vh,calc(100vh-320px))] overflow-y-auto pr-1 -mr-1"
      >
        <InfiniteScroll
          dataLength={deliveryList.length}
          next={loadMore}
          hasMore={hasMore}
          scrollableTarget="scrollableDiv"
          loader={
            <div className="text-center py-4 text-gray-500 text-sm">
              {t("loading_more")}
            </div>
          }
          endMessage={
            !initialLoading && deliveryList.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm rounded-xl border border-dashed border-gray-200 bg-white/80">
                {t("no_deliveries_found")}
              </div>
            
            ) : null
          }
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 pb-4">
            {deliveryList.map((order, idx) => {
              const deadline = order?.deliveries?.[0]?.deadline;
              const deadlineLabel =
                deadline && moment(deadline).isValid()
                  ? moment(deadline).format("DD/MM/YYYY")
                  : "—";
                  const delivery = order?.deliveries?.[0];
                  const priority = delivery?.priority?.toLowerCase();
                  const priorityColor = priorityColorMap[priority] || "bg-gray-400";

              return (
                <Card
                  key={order?.id ?? order?.order_id ?? idx}
                  className="shadow-sm border border-gray-100 hover:shadow-md transition-shadow bg-gradient-to-r from-[rgba(253,243,221,1)] to-[rgba(255,255,255,1)]"
                >
                  <CardBody className="p-4 sm:p-5">
                    <div className="flex justify-between items-start gap-2 mb-4">
                        <Typography
                          variant="h6"
                          color="blue-gray"
                          className="font-bold text-base sm:text-lg"
                        >
                          #{order.order_id}
                        </Typography>

                        {/* PRIORITY / PAYMENT STATUS + DATE SECTION */}
                        <div className="flex flex-col items-end gap-2">
                          {activeTab === "completed" ? (
                            order?.payment_status && (
                              <div
                                className={`px-3 py-1 rounded-full flex items-center justify-center whitespace-nowrap ${
                                  order.payment_status === "paid"
                                    ? "bg-green-100"
                                    : order.payment_status === "partial"
                                    ? "bg-[#FEF9C3]"
                                    : order.payment_status === "pending"
                                    ? "bg-[#F4B11F1A]"
                                    : "bg-gray-100"
                                }`}
                              >
                                <span // This is a dynamic value from a function, so it's tricky to translate directly. The formatPaymentStatus function handles this.
                                  className={`text-[12px] font-medium capitalize ${
                                    order.payment_status === "paid"
                                      ? "text-green-700"
                                      : order.payment_status === "partial"
                                      ? "text-yellow-700"
                                      : order.payment_status === "pending"
                                      ? "text-[#F4B11F]"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {formatPaymentStatus(order.payment_status)}
                                </span>
                              </div>
                            )
                          ) : (
                            priority && (
                              <div className="px-3 py-1 rounded-full bg-[#E8F1EE] flex items-center justify-center whitespace-nowrap">
                                <span className="text-[10px] font-medium text-[#065C36] capitalize">
                                  {t("priority_label", { priority: priority })}
                                </span>
                              </div>
                            )
                          )}

                          <Typography className="text-sm text-gray-400 font-normal">
                            {deadlineLabel}
                          </Typography>
                        </div>
                      </div>
                    {(activeTab === 'assigned' || activeTab === 'in_progress') && 
                      <div className="space-y-3 mb-6">
                        <div className="flex gap-3 min-w-0">
                          <img
                            src="/img/icons/Container-pin.png"
                            alt=""
                            className="h-5 w-5 shrink-0 mt-0.5"
                          />
                          <Typography className="text-sm text-gray-600 font-medium break-words">
                            {order?.deliveries?.[0]?.rfq_product_vendor
                              ?.pickup_location || "—"}
                          </Typography>
                          
                        </div>
                        <div className="flex gap-3 min-w-0">
                          <div className="h-5 w-5 flex items-center justify-center shrink-0 mt-0.5">
                            <img
                              src="/img/icons/Container.png"
                              alt=""
                              className="w-5 h-5"
                            />
                          </div>
                          <Typography className="text-sm text-gray-600 font-medium break-words">
                            {order?.deliveries?.[0]?.delivery_address || "—"}
                          </Typography>
                        </div>
                        <div className="flex gap-3">
                          <img
                            src="/img/icons/Container-1.png"
                            alt=""
                            className="h-5 w-5 shrink-0 mt-0.5"
                          />
                          <Typography className="text-sm text-gray-600 font-medium">
                            {t("items_count", { count: order?.items_count ?? "—" })}
                          </Typography>
                        </div>
                      </div>
                    }

                    <div className="flex flex-wrap justify-between items-center gap-2">
                      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white max-w-full">
                        {order?.is_installation === "yes" && (
                          <>
                            <img
                              src="/img/icons/Container-3.png"
                              alt=""
                              className="h-3 w-3 shrink-0"
                            />
                            <span className="text-[10px] font-bold text-[#2F302C] truncate">
                              {t("delivery_and_installation")}
                            </span>
                          </>
                        )}
                        {order?.is_installation === "no" && (
                          <>
                            <TruckIcon className="h-4 w-4 text-[#F4B11F] shrink-0" />
                            <span className="text-[10px] font-bold text-[#F4B11F]"> 
                              {t("delivery")}
                            </span>
                          </>
                        )}
                      </div>
                      <Link
                        to={
                          activeTab === "completed"
                            ? `/delivery-partner/completed-summary/${order?.id}?tab=completed`
                            : `/delivery-partner/assigned-delivery/${order?.id}`
                        }
                        className="text-xs font-bold text-[#065C36] underline shrink-0"
                      > 
                        {t("view_more")}
                      </Link>
                    </div>
                  </CardBody>
                </Card>
              );
            })}
          </div>
        </InfiniteScroll>
      </div>
      <RejectedProofModal
        open={openRejectedModal}
        onClose={() => setOpenRejectedModal(false)}
        items={Array.isArray(rejected_assignments) ? rejected_assignments : []}
        loading={rejected_loading}
      />
    </div>
  );
}

export default DeliveryHomePage;
